<template>
  <div>
    <AddParticipantFieldsContextMenu
      @hide="isManageMode = false"
      class="add-participant-fields-context-menu-button inline-block"
    >
      <template #trigger>
        <aha-antd-button
          name="open-dropdown-participant-field-button"
          v-aha-emit-action
          icon-name="plus-new"
          icon-view-box="0 0 16 16"
          variant="tertiary"
          size="md"
        >
        </aha-antd-button>
      </template>
      <template #items>
        <aha-context-menu-item
          v-for="field in fields"
          :key="`${isManageMode}-${field.id}`"
          :name="isManageMode ? 'delete-participant-field' : 'add-participant-field'"
          v-aha-emit-action="{
            participant_field: field.name,
            participant_field_type: field.fieldType,
          }"
          :class="{
            'selected': field.selected && !isManageMode,
            'hidden': isManageMode && field.isSystemField,
            'capitalize': field.isSystemField,
          }"
          @click="(event) => handleClickField(field, event)"
        >
          <div class="flex flex-1 items-center justify-between gap-4">
            <span
              :title="field.name"
              class="min-w-24 max-w-28 truncate"
            >{{ field.name }}</span>
            <aha-icon
              v-if="isManageMode && field.fieldType === 'customField'"
              name="trash-new"
              width="16"
              height="16"
              viewBox="0 0 24 24"
            />
          </div>
        </aha-context-menu-item>
        <aha-context-menu-item v-if="showAddCustomFieldButton" @click="isShownAddFieldModal = true">
          <div class="flex flex-1 items-center justify-between gap-4">
            <span
              :title="$i18nPresenter.t('Custom field')"
              class="min-w-24 max-w-28 truncate"
            >{{ $i18nPresenter.t('Custom field') }}</span>
            <aha-icon
              name="plus-new"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            />
          </div>
        </aha-context-menu-item>
        <aha-context-menu-item
          v-if="isShownManageButton"
          @click.stop
          class="border-t border-base-30"
        >
          <button type="button" class="text-center flex-1 w-full" @click="isManageMode = !isManageMode">
            <span :title="bottomLabel" class="min-w-24 max-w-28 truncate">{{ bottomLabel }}</span>
          </button>
        </aha-context-menu-item>
      </template>
    </AddParticipantFieldsContextMenu>
    <aha-modal
      v-if="isShownAddFieldModal"
      width="504px"
      :title="$i18nPresenter.t('Add a custom field')"
      noMarginPadding
      @hide="isShownAddFieldModal = false"
    >
      <AddCustomField
        name="participant-custom-field-modal"
        v-aha-emit-action.view
        @cancel="isShownAddFieldModal = false"
        @create="handleCreateCustomField"
      >
      </AddCustomField>
    </aha-modal>
    <aha-modal
      v-if="selectedDeleteItem"
      :title="$i18nPresenter.t('Delete this field everywhere?')"
      @hide="selectedDeleteItem = null"
    >
      <p>{{ modalConfirmDeleteContent }}</p>
      <template #action>
        <aha-antd-button
          variant="secondary"
          @click="selectedDeleteItem = null"
        >
          {{ $i18nPresenter.t('Cancel') }}
        </aha-antd-button>
        <aha-antd-button
          variant="danger"
          class="ml-4"
          @click="deleteField(selectedDeleteItem)"
        >
          {{ $i18nPresenter.t('Delete') }}
        </aha-antd-button>
      </template>
    </aha-modal>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import AddParticipantFieldsContextMenu from '@/components-v2/presentation-editor/side-right/AddParticipantFieldsContextMenu.vue'
import AddCustomField from '@/components-v2/presentation-editor/side-right/AddCustomField.vue'

export default {
  components: {
    AddParticipantFieldsContextMenu,
    AddCustomField,
  },
  props: {
    presentationId: {
      type: Number,
      default: null,
    },
    selectedFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isManageMode: false,
      isShownAddFieldModal: false,
      selectedDeleteItem: null,
    }
  },
  computed: {
    ...mapGetters('presentationSetting', ['participantFields', 'maxAllowedField']),
    ...mapState({
      slides: (state) => state.presentation.slides,
    }),
    modalConfirmDeleteContent() {
      return this.$i18nPresenter.t('This field will be removed from all slides and settings where it appears to the audience.')
    },
    showAddCustomFieldButton() {
      return !this.isManageMode && !this.isReachedMaxAllowedField
    },
    isShownManageButton() {
      if (!this.isManageMode) {
        return this.participantFields?.filter((field) => !field.isSystemField).length > 0
      }

      return true
    },
    bottomLabel() {
      return this.isManageMode ? this.$i18nPresenter.t('Done') : this.$i18nPresenter.t('Manage')
    },
    fields() {
      return this.participantFields.map((f) => {
        return {
          ...f,
          selected: this.selectedFields.some(({ id }) => id === f.id),
        }
      })
    },
    isReachedMaxAllowedField() {
      return this.fields.length >= this.maxAllowedField
    },
  },
  methods: {
    ...mapActions('presentationSetting', ['updateParticipantInfoFields', 'deletePresentationParticipantField']),
    ...mapActions('slideSettingModule', ['deleteSlideParticipantField']),
    deleteField(field) {
      // delete presentation participant field and presentation selected participant field
      this.deletePresentationParticipantField({
        presentationId: this.presentationId,
        fieldId: field.id,
      })

      // delete slides participant field
      this.slides.forEach((slide) => {
        this.deleteSlideParticipantField({
          slide,
          fieldId: field.id,
        })
      })

      this.selectedDeleteItem = null
      this.$emit('delete', field)
    },
    formatField(field) {
      return {
        id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
        type: field.name,
        fieldType: 'customField',
        hidden: false,
        ...field,
      }
    },
    handleCreateCustomField(field) {
      const formattedField = this.formatField(field)

      const updatedFields = {
        fields: [
          ...this.participantFields,
          formattedField,
        ],
      }

      this.updateParticipantInfoFields({
        presentationId: this.presentationId,
        updatedFields,
      })

      this.$emit('create', formattedField)
      this.isShownAddFieldModal = false
    },
    handleClickField(field) {
      if (this.isManageMode && field.isSystemField) return

      if (this.isManageMode) {
        this.selectedDeleteItem = field
        return
      }

      this.$emit('select', this.formatField(field))
    },
  },
}
</script>
<style lang="scss" scoped>
.flex {
    display: flex;
    flex-basis: auto;
}

.selected {
    @apply text-base-55;
    pointer-events: none;
}
</style>
